import React, { type RefObject } from 'react';

import { Flex, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';

import { AWAY_TEXT_COLOR } from './awayService';

type Props = {
  buttonRef: RefObject<HTMLButtonElement | null>;
  onPress: () => void;
  isExpanded: boolean;
  label: string;
  comingSoonLabel?: string;
  price?: { beforeText: string; formatted: string; afterText: string };
  serviceLabel: string;
  isAway: boolean;
  datesLabel: string;
};

/** The footer's price, service and dates, which open the booking details sheet. */
const RevampMobileCtaBarDetails = ({
  buttonRef,
  onPress,
  isExpanded,
  label,
  comingSoonLabel,
  price,
  serviceLabel,
  isAway,
  datesLabel,
}: Props): JSX.Element => (
  <Flex
    ref={buttonRef}
    gap="2x"
    webAs="button"
    flexDirection="column"
    alignItems="flex-start"
    justifyContent="center"
    alignSelf="stretch"
    flexGrow="1"
    border="none"
    padding="0x"
    data-testid="revamp-mobile-cta-bar-details"
    onPress={onPress}
    aria-haspopup="dialog"
    aria-expanded={isExpanded}
    aria-label={label}
    sx={{ minWidth: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
  >
    {comingSoonLabel ? (
      <Badge
        boxProps={{ testID: 'cta-coming-soon-badge', alignSelf: 'flex-start' }}
        text={comingSoonLabel}
        variant="default"
      />
    ) : (
      price && (
        <Text
          size="200"
          fontWeight="regular"
          textColor="secondary"
          data-testid="revamp-mobile-cta-bar-price"
          aria-hidden="true"
        >
          {price.beforeText && `${price.beforeText} `}
          <Text
            size="300"
            fontWeight="semibold"
            textColor="price"
            sx={{ textDecoration: 'underline' }}
          >
            {price.formatted}
          </Text>
          {` ${price.afterText}`}
        </Text>
      )
    )}

    <Flex alignItems="center" gap="2x" flexWrap="wrap" aria-hidden="true">
      <Text
        size="200"
        fontWeight="semibold"
        textColor={isAway ? AWAY_TEXT_COLOR : 'primary'}
        data-testid="revamp-mobile-cta-bar-service"
        sx={{ textDecoration: 'underline' }}
      >
        {serviceLabel}
      </Text>
      {/* Grouped so the separator wraps with the dates it separates, rather than
          being left dangling at the end of the first line. */}
      <Flex alignItems="center" gap="2x">
        <Text size="200" textColor="secondary">
          ·
        </Text>
        <Text size="200" textColor="secondary" data-testid="revamp-mobile-cta-bar-dates">
          {datesLabel}
        </Text>
      </Flex>
    </Flex>
  </Flex>
);

export default RevampMobileCtaBarDetails;
